import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

const CSATPYQAnalysis = ({ isPro, onBack }) => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={onBack}>← Back to CSAT Suite</Button>
          <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>{isPro ? "PRO" : "FREE"}</Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">PYQ Intelligence</h1>
          <p className="text-slate-500">
            15-25 years trend analysis • Trap patterns • Attempt guidance
          </p>
        </div>

        {/* PYQ Trends */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-base font-medium text-slate-900 mb-4">Section-wise PYQ Distribution</h3>
              <div className="space-y-3">
                {[
                  { section: "Reading Comprehension", percentage: 30, trend: "↗️ Rising" },
                  { section: "Logical Reasoning", percentage: 25, trend: "→ Stable" },
                  { section: "Quantitative Aptitude", percentage: 20, trend: "↘️ Declining" },
                  { section: "Data Interpretation", percentage: 15, trend: "→ Stable" },
                  { section: "Decision Making", percentage: 10, trend: "↗️ Rising" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <span className="text-sm font-medium text-slate-700">{item.section}</span>
                        <span className="text-xs text-slate-500">{item.trend}</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-slate-700"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-slate-900 w-10">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-6">
              <h3 className="text-base font-medium text-slate-900 mb-4">PYQ-Backed Attempt Order</h3>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="bg-green-100 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Reading Comprehension First</p>
                    <p className="text-xs text-slate-600">Safest scoring zone - stabilizes confidence</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-yellow-100 text-yellow-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Logical Reasoning Next</p>
                    <p className="text-xs text-slate-600">Quick wins with elimination logic</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-slate-100 text-slate-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Decision Making</p>
                    <p className="text-xs text-slate-600">Usually straightforward with clear choices</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="bg-red-100 text-red-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Quant & DI Last</p>
                    <p className="text-xs text-slate-600">Time-consuming - attempt only easy ones</p>
                  </div>
                </li>
              </ol>
            </CardContent>
          </Card>
        </div>

        {/* Trap Patterns */}
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <h3 className="text-base font-medium text-slate-900 mb-4">PYQ Trap Patterns (Must Know)</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-red-900 mb-2">DI Traps</h4>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• Base vs Percentage confusion</li>
                  <li>• CAGR vs Simple growth</li>
                  <li>• Missing data points</li>
                  <li>• Scale inconsistencies in graphs</li>
                </ul>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-yellow-900 mb-2">Reasoning Traps</h4>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• Syllogism: All vs Some confusion</li>
                  <li>• Direction: East-West mirror images</li>
                  <li>• Blood relations: Gender assumptions</li>
                  <li>• Coding: Pattern breaks</li>
                </ul>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <h4 className="font-semibold text-sm text-slate-900 mb-2">RC Traps</h4>
                <ul className="text-xs text-slate-700 space-y-1">
                  <li>• Complex sequence patterns (skip these)</li>
                  <li>• Venn diagram with 4+ sets</li>
                  <li>• Seating arrangement with many constraints</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CSATPYQAnalysis;
